// This helper is separated here as it is accessed by other components (select, toggle, boolean)
export interface CustomChangeBroadcastPayload {
  name: string
  value: string
}

type ChangeCallback = (eventPayload: CustomChangeBroadcastPayload | null) => void

export function broadcastInputChange(input: HTMLInputElement): void {
  const eventPayload = { name: input.name }
  const form = input.form
  if (form === null) return
  form.dispatchEvent(new CustomEvent('ui-input:change', { detail: eventPayload }))
}

export function listenToChangesOnForm(
  form: HTMLFormElement | null,
  onChange: ChangeCallback,
  hasInitialCheck = true
): void {
  if (form === null) return
  form?.addEventListener('ui-input:change', e => {
    const event = e as CustomEvent<CustomChangeBroadcastPayload>
    onChange(event.detail)
  })
  if (hasInitialCheck) onChange(null)
}

export function fieldIsMultiple(eventPayload: CustomChangeBroadcastPayload | null): boolean {
  return /(.+)\[\]$/.test(eventPayload?.name ?? '')
}

export function shouldSkipCheckForChanges(
  eventPayload: CustomChangeBroadcastPayload | null,
  isMultiple: boolean,
  dependencyFieldNames: string[]
): boolean {
  const nameNormalised = isMultiple
    ? eventPayload?.name?.replace(/(.+)\[]$/, '$1')
    : eventPayload?.name

  // if event is null, we should not skip because we're in the initial check
  if (eventPayload === null) return false
  // if there are no dependencies, we should skip
  if (dependencyFieldNames.length === 0) return true
  // check if we have at least one match
  return !dependencyFieldNames.some(fieldName => fieldName === nameNormalised)
}

export function dependencyWithModelParent(
  originalDependencies: string[],
  modelParent: string
): string[] {
  return originalDependencies.map(dependency => modelParent + '[' + dependency + ']')
}

function queryStringFromDependencyData(
  formData: FormData,
  dependencyFieldNames: string[],
  modelParent?: string,
  additionalParameters?: Record<string, string>
): string {
  const queryLines: string[] = []
  dependencyFieldNames.forEach(fieldName => {
    const fieldNameNormalised =
      typeof modelParent === 'string' && modelParent.length > 0
        ? modelParent + '[' + fieldName + ']'
        : fieldName
    const isMultiple = new Set(formData.keys()).has(fieldNameNormalised + '[]')
    if (isMultiple) {
      formData.getAll(fieldNameNormalised + '[]')?.forEach(value => {
        queryLines.push(fieldName + '[]=' + encodeURIComponent((value as string) ?? ''))
      })
    } else {
      queryLines.push(
        fieldName + '=' + encodeURIComponent((formData.get(fieldNameNormalised) as string) ?? '')
      )
    }
  })
  if (additionalParameters !== undefined && Object.keys(additionalParameters).length > 0) {
    Object.entries(additionalParameters).forEach(([key, value]) => {
      queryLines.push(`${key}=${value}`)
    })
  }
  return queryLines.join('&')
}

export function urlFromDependencyData(
  path: string,
  form: HTMLFormElement | null,
  dependencyFieldNames: string[],
  modelParent?: string,
  additionalParameters?: Record<string, string>
): URL | null {
  if (form === null) return null

  const formData = new FormData(form)
  const url = new URL(path, window.location.origin)
  url.search = queryStringFromDependencyData(
    formData,
    dependencyFieldNames,
    modelParent,
    additionalParameters
  )
  return url
}
